/**
 * Utility functions for pagination parameter normalization
 */

export interface PaginationParams {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
  search?: string
  [key: string]: any
}

/**
 * Normalizes pagination parameters to a consistent format
 * This ensures all services use the same parameter structure
 * 
 * Special handling:
 * - When limit is -1, it's included in parameters (backend handles no limit)
 * - All other parameters are included as-is
 */
export function normalizePaginationParams(params: PaginationParams): PaginationParams {
  const normalized: PaginationParams = {}

  // Always include pagination parameters
  if (params.page !== undefined) normalized.page = params.page
  // Always include limit parameter, including -1 (which means no limit)
  if (params.limit !== undefined) normalized.limit = params.limit
  // Only include sorting parameters if sortBy is not empty
  if (params.sortBy !== undefined && params.sortBy !== '') normalized.sortBy = params.sortBy
  if (params.sortOrder !== undefined && params.sortBy !== undefined && params.sortBy !== '') normalized.sortOrder = params.sortOrder
  if (params.search !== undefined) normalized.search = params.search

  // Include any additional filters
  Object.keys(params).forEach(key => {
    if (!['page', 'limit', 'sortBy', 'sortOrder', 'search'].includes(key)) {
      normalized[key] = params[key]
    }
  })

  return normalized
}

/**
 * Builds query parameters for API calls
 * Uses the direct format: ?page=1&limit=10&sortBy=id&sortOrder=DESC
 */
export function buildQueryParams(params: PaginationParams): string {
  const queryParams = new URLSearchParams()
  
  Object.keys(params).forEach(key => {
    const value = params[key]
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value.toString())
    }
  })

  return queryParams.toString()
}

/**
 * Builds nested query parameters for API calls that expect the params[field] format
 * Uses the nested format: ?params[page]=1&params[limit]=10&params[sortBy]=id&params[sortOrder]=DESC
 */
export function buildNestedQueryParams(params: PaginationParams): string {
  const queryParams = new URLSearchParams()
  
  Object.keys(params).forEach(key => {
    const value = params[key]
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(`params[${key}]`, value.toString())
    }
  })

  return queryParams.toString()
}

/**
 * Determines which parameter format to use based on the endpoint
 * Some endpoints expect direct parameters, others expect separated parameters
 */
export function getParameterFormat(endpoint: string): 'direct' | 'nested' | 'separated' {
  // List of endpoints that use separated parameter format (new format)
  const separatedEndpoints = [
    '/users',
    '/accounts',
    '/campaigns',
    '/advertisers',
    '/templates',
    '/msisdn-validators',
    '/operators',
    '/countries',
    '/categories',
    '/profiles',
    '/privileges',
    '/subscription-services'
  ]

  // List of endpoints that use old nested parameter format
  const nestedEndpoints: string[] = [
    // Add any endpoints that still use the old nested format
  ]

  // Check if the endpoint uses separated format (new format)
  const usesSeparated = separatedEndpoints.some(separatedEndpoint => 
    endpoint.includes(separatedEndpoint)
  )

  // Check if the endpoint uses nested format (old format)
  const usesNested = nestedEndpoints.some(nestedEndpoint => 
    endpoint.includes(nestedEndpoint)
  )

  if (usesSeparated) return 'separated'
  if (usesNested) return 'nested'
  return 'direct'
}

/**
 * Builds query parameters with separated pagination and filters (new format)
 * Pagination goes in params[field] and filters go in filters[field]
 * Uses the format: ?params[page]=1&params[limit]=10&filters[search]=demo&filters[operator_id]=1
 */
export function buildSeparatedQueryParams(params: PaginationParams): string {
  const queryParams = new URLSearchParams()
  
  // Separate pagination/sorting parameters from filters
  const paginationParams: PaginationParams = {}
  const filterParams: PaginationParams = {}
  
  Object.keys(params).forEach(key => {
    const value = params[key]
    if (value !== undefined && value !== null && value !== '') {
      if (['page', 'limit', 'sortBy', 'sortOrder'].includes(key)) {
        paginationParams[key] = value
      } else {
        filterParams[key] = value
      }
    }
  })
  
  // Add pagination parameters with params[field] format
  Object.keys(paginationParams).forEach(key => {
    queryParams.append(`params[${key}]`, paginationParams[key].toString())
  })
  
  // Add filter parameters with filters[field] format
  Object.keys(filterParams).forEach(key => {
    queryParams.append(`filters[${key}]`, filterParams[key].toString())
  })
  
  return queryParams.toString()
}

/**
 * Builds the appropriate query string based on the endpoint
 */
export function buildQueryString(endpoint: string, params: PaginationParams): string {
  const format = getParameterFormat(endpoint)
  const normalizedParams = normalizePaginationParams(params)
  
  if (format === 'separated') {
    return buildSeparatedQueryParams(normalizedParams)
  } else if (format === 'nested') {
    return buildNestedQueryParams(normalizedParams)
  } else {
    return buildQueryParams(normalizedParams)
  }
}
